import React, { useState } from 'react';
import ClubSearch from '../../components/shared/ClubSearch';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getClubs, createClub, updateClub, deleteClub } from '../../services/api';
import { storage } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const ManageClubs = () => {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClub, setEditingClub] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        city: '',
        numberOfCourts: 4,
        postalCode: '',
        latitude: null,
        longitude: null,
        amenities: [],
        slug: '',
        primaryColor: '#06b6d4',
        logoUrl: '',
        bannerUrl: '',
        isPublicSpace: false
    });

    const [verifyingGeo, setVerifyingGeo] = useState(false);
    const [geoResult, setGeoResult] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);

    const userId = localStorage.getItem('userId');
    const { data, isLoading, error } = useQuery({
        queryKey: ['my-clubs', userId],
        queryFn: () => getClubs({ organizerId: userId })
    });

    const clubs = data || [];

    const mutation = useMutation({
        mutationFn: (newClub) => editingClub ? updateClub(editingClub.id, newClub) : createClub(newClub),
        onSuccess: () => {
            queryClient.invalidateQueries(['my-clubs']);
            setIsModalOpen(false);
            setEditingClub(null);
            resetForm();
            alert(editingClub ? 'Club actualizado con éxito' : 'Club creado con éxito');
        },
        onError: (error) => {
            console.error('Error saving club:', error);
            alert('Error al guardar el club: ' + (error.response?.data?.message || error.message));
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteClub(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['my-clubs']);
            alert('Club eliminado con éxito');
        },
        onError: (error) => {
            console.error('Error deleting club:', error);
            alert('Error al eliminar el club: ' + (error.response?.data?.message || error.message));
        }
    });

    const resetForm = () => {
        setFormData({
            name: '', address: '', city: '', postalCode: '', numberOfCourts: 4, phone: '', amenities: [],
            slug: '', primaryColor: '#06b6d4', logoUrl: '', bannerUrl: '', isPublicSpace: false
        });
        setGeoResult(null);
    };

    const handleEdit = (club) => {
        setEditingClub(club);
        setFormData({
            name: club.name,
            address: club.address,
            city: club.city,
            postalCode: club.postalCode || '',
            numberOfCourts: club.numberOfCourts,
            phone: club.phone || '',
            amenities: club.amenities || [],
            slug: club.slug || '',
            primaryColor: club.primaryColor || '#06b6d4',
            logoUrl: club.logoUrl || '',
            bannerUrl: club.bannerUrl || '',
            isPublicSpace: club.isPublicSpace || false
        });
        setGeoResult(club.latitude ? { lat: club.latitude, lon: club.longitude } : null);
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const clubDataWithOrganizer = {
            ...formData,
            organizerId: userId
        };
        mutation.mutate(clubDataWithOrganizer);
    };

    // Manejar la selección de un club desde el buscador nacional
    const handleClubSelect = (club) => {
        if (!club) {
            setFormData(prev => ({
                ...prev,
                name: '',
                address: '',
                phone: ''
            }));
            return;
        }

        let fullAddress = club.d || '';
        let street = fullAddress;
        let city = '';
        let postalCode = '';

        // Dividir por guiones para separar Calle - Municipio (CP) - Provincia
        const parts = fullAddress.split(' - ');
        
        if (parts.length >= 2) {
            street = parts[0].trim();
            const cityCpPart = parts[1].trim();
            
            // Extraer CP entre paréntesis
            const cpMatch = cityCpPart.match(/\((\d{5})\)/);
            if (cpMatch) {
                postalCode = cpMatch[1];
                city = cityCpPart.replace(cpMatch[0], '').trim();
            } else {
                city = cityCpPart;
            }
        }

        setFormData(prev => ({
            ...prev,
            name: club.n,
            address: street,
            city: city || prev.city,
            postalCode: postalCode || prev.postalCode,
            phone: club.t || prev.phone
        }));
    };

    const handleVerifyAddress = async () => {
        if (!formData.address || !formData.city) {
            alert('Introduce dirección y ciudad para verificar');
            return;
        }

        setVerifyingGeo(true);
        setGeoResult(null);
        try {
            // Use structured query for better results
            const params = new URLSearchParams({
                street: formData.address,
                city: formData.city,
                postalcode: formData.postalCode || '',
                country: 'Spain',
                format: 'json',
                limit: '1'
            });

            const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
            const data = await response.json();
            
            if (data && data.length > 0) {
                const result = { 
                    lat: parseFloat(data[0].lat), 
                    lon: parseFloat(data[0].lon),
                    displayName: data[0].display_name
                };
                setGeoResult(result);
                // Pre-update formData with coords to ensure they are saved even if bg geocoding fails
                setFormData(prev => ({ ...prev, latitude: result.lat, longitude: result.lon }));
            } else {
                alert('No se pudo encontrar la ubicación exacta. Intenta ajustar la dirección o introduce las coordenadas manualmente.');
            }
        } catch (error) {
            console.error('Error verifying address:', error);
            alert('Error al conectar con el servicio de mapas. Puedes introducir las coordenadas manualmente si las conoces.');
        } finally {
            setVerifyingGeo(false);
        }
    };

    const handleFileUpload = async (file, type) => {
        if (!file) return;
        
        // Validaciones
        const isLogo = type === 'logo';
        const maxSize = isLogo ? 2 * 1024 * 1024 : 5 * 1024 * 1024; // 2MB vs 5MB
        
        if (file.size > maxSize) {
            alert(`El archivo es demasiado grande. Máximo ${isLogo ? '2MB' : '5MB'}.`);
            return;
        }

        const setter = isLogo ? setUploadingLogo : setUploadingBanner;
        setter(true);

        try {
            const storageRef = ref(storage, `clubs/${userId}/${type}_${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            setFormData(prev => ({
                ...prev,
                [isLogo ? 'logoUrl' : 'bannerUrl']: downloadURL
            }));
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Error al subir la imagen');
        } finally {
            setter(false);
        }
    };

    const toggleAmenity = (amenity) => {
        setFormData(prev => ({
            ...prev,
            amenities: prev.amenities.includes(amenity)
                ? prev.amenities.filter(a => a !== amenity)
                : [...prev.amenities, amenity]
        }));
    };

    const amenityList = [
        { id: 'bar', label: '☕ Bar/Cafetería' },
        { id: 'vestuarios', label: '🚿 Vestuarios' },
        { id: 'parking', label: '🚗 Parking' },
        { id: 'tienda', label: '🛍️ Tienda' },
        { id: 'wifi', label: '📶 Wifi Gratis' },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2">Mis Clubes</h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Gestiona las sedes donde organizas tus pozos.</p>
                </div>
                <button
                    onClick={() => { resetForm(); setEditingClub(null); setIsModalOpen(true); }}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                    <span>+</span> Añadir Nuevo Club
                </button>
            </div>

            {/* Vista Desktop: Tabla */}
            <div className="hidden md:block bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-400 dark:text-gray-500 text-xs font-black uppercase tracking-widest">
                            <tr>
                                <th className="px-6 py-4">Club</th>
                                <th className="px-6 py-4">Ubicación</th>
                                <th className="px-6 py-4">Pistas</th>
                                <th className="px-6 py-4">Servicios</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                            {isLoading ? (
                                [1, 2, 3].map(i => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-10 bg-gray-50/30 dark:bg-slate-700/30"></td>
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center text-red-500 font-bold">
                                        Error al cargar los clubes: {error.message}
                                    </td>
                                </tr>
                            ) : clubs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center text-gray-500 font-bold">
                                        No has creado ningún club todavía.
                                    </td>
                                </tr>
                            ) : (
                                clubs.map((club) => (
                                    <tr key={club.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors">
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                {club.logoUrl ? (
                                                    <img src={club.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-lg bg-gray-100 dark:bg-slate-700" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-lg">
                                                        🏢
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-black text-gray-900 dark:text-white uppercase tracking-tight">{club.name}</div>
                                                    {club.slug && (
                                                        <div className="text-xs text-primary-600 dark:text-primary-400 font-bold">/c/{club.slug}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm font-bold text-gray-700 dark:text-gray-300">{club.city}</div>
                                                {club.latitude && club.longitude ? (
                                                    <span className="text-xs bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded border border-green-100 dark:border-green-500/20 flex items-center gap-0.5" title="Geolocalizado correctamente">
                                                        📍 <span className="font-black">OK</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-xs bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded border border-amber-100 dark:border-amber-500/20 flex items-center gap-0.5" title="No geolocalizado (se intentará en la próxima edición)">
                                                        ⚠️ <span className="font-black">?</span>
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[180px]">{club.address}</div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className="bg-gray-50 dark:bg-slate-700 px-3 py-1 rounded-lg border border-gray-100 dark:border-slate-600 text-sm font-bold text-gray-700 dark:text-gray-300">
                                                🎾 {club.numberOfCourts}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                {club.amenities && club.amenities.slice(0, 2).map(a => (
                                                    <span key={a} className="text-[10px] font-black uppercase tracking-widest text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-2 py-0.5 rounded border border-primary-100 dark:border-primary-500/20">
                                                        {a}
                                                    </span>
                                                ))}
                                                {club.amenities && club.amenities.length > 2 && (
                                                    <span className="text-[10px] font-bold text-gray-400">
                                                        +{club.amenities.length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-right space-x-2">
                                            <button
                                                onClick={() => handleEdit(club)}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all font-bold"
                                                title="Editar Club"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => { if (window.confirm('¿Eliminar este club? Esta acción no se puede deshacer.')) deleteMutation.mutate(club.id) }}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all font-bold"
                                                title="Eliminar"
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Vista Mobile: Cards */}
            <div className="md:hidden space-y-4">
                {isLoading ? (
                    [1, 2, 3].map(i => (
                        <div key={i} className="bg-white rounded-2xl p-4 animate-pulse h-32"></div>
                    ))
                ) : error ? (
                    <div className="bg-white rounded-2xl p-6 text-center text-red-500 font-bold">
                        Error al cargar los clubes: {error.message}
                    </div>
                ) : clubs.length === 0 ? (
                    <div className="bg-white rounded-2xl p-8 text-center text-gray-500 font-bold">
                        No has creado ningún club todavía.
                    </div>
                ) : (
                    clubs.map((club) => (
                        <div key={club.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm p-4 transition-colors">
                            <div className="flex items-start gap-3 mb-3">
                                {club.logoUrl ? (
                                    <img src={club.logoUrl} alt="Logo" className="w-12 h-12 object-contain rounded-lg bg-gray-100 dark:bg-slate-700 flex-shrink-0" />
                                ) : (
                                    <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-xl flex-shrink-0">
                                        🏢
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-tight truncate">{club.name}</h3>
                                    {club.slug && (
                                        <p className="text-xs text-primary-600 dark:text-primary-400 font-bold">/c/{club.slug}</p>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">📍 {club.city}</p>
                                        {club.latitude && club.longitude ? (
                                            <span className="text-[10px] bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-1 py-0.5 rounded font-black">📍 OK</span>
                                        ) : (
                                            <span className="text-[10px] bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1 py-0.5 rounded font-black">📍 NO</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <span className="bg-gray-50 dark:bg-slate-700 px-3 py-1 rounded-lg border border-gray-100 dark:border-slate-600 text-sm font-bold text-gray-700 dark:text-gray-300">
                                    🎾 {club.numberOfCourts} Pistas
                                </span>
                                {club.amenities && club.amenities.slice(0, 2).map(a => (
                                    <span key={a} className="text-[10px] font-black uppercase text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-2 py-1 rounded border border-primary-100 dark:border-primary-500/20">
                                        {a}
                                    </span>
                                ))}
                            </div>

                            <div className="flex gap-2 border-t border-gray-100 dark:border-slate-700 pt-3">
                                <button
                                    onClick={() => handleEdit(club)}
                                    className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all text-sm font-bold"
                                >
                                    ✏️ Editar
                                </button>
                                <button
                                    onClick={() => { if (window.confirm('¿Eliminar este club?')) deleteMutation.mutate(club.id) }}
                                    className="inline-flex items-center justify-center w-10 h-10 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal de Formulario */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 dark:bg-black/80 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[3rem] shadow-2xl w-full max-w-2xl max-h-[90vh] animate-slide-in-up overflow-hidden relative flex flex-col transition-colors">
                        <div className="overflow-y-auto p-6 sm:p-8 md:p-12">
                            <div className="flex justify-between items-center mb-6 sm:mb-10">
                                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white">
                                    {editingClub ? 'Editar Club' : 'Nuevo Club'}
                                </h2>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-2xl">✕</button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                    <div className="space-y-2 md:col-span-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Nombre del Club (Busca en España 🇪🇸)</label>
                                        <ClubSearch 
                                            onSelect={handleClubSelect} 
                                            initialValue={formData.name}
                                            placeholder="Busca por nombre para autocompletar..."
                                        />
                                        <input
                                            required
                                            type="hidden" // El valor real se gestiona por el ClubSearch o este input si el usuario escribe
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Nº de Pistas</label>
                                        <input
                                            type="number"
                                            value={formData.numberOfCourts}
                                            onChange={(e) => setFormData({ ...formData, numberOfCourts: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold dark:text-white"
                                        />
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Dirección Completa</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.address}
                                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold dark:text-white"
                                            placeholder="Calle Deporte, 45"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Ciudad</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.city}
                                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold dark:text-white"
                                            placeholder="Ej: Madrid"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Código Postal</label>
                                        <input
                                            type="text"
                                            value={formData.postalCode}
                                            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold dark:text-white"
                                            placeholder="28001"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Teléfono</label>
                                        <input
                                            type="text"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold dark:text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Latitud</label>
                                        <input
                                            type="number"
                                            step="any"
                                            value={formData.latitude || ''}
                                            onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : null })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold text-sm dark:text-white"
                                            placeholder="40.4168"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Longitud</label>
                                        <input
                                            type="number"
                                            step="any"
                                            value={formData.longitude || ''}
                                            onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : null })}
                                            className="w-full bg-gray-50 dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:bg-white dark:focus:bg-slate-800 focus:border-primary-500 transition-all outline-none font-bold text-sm dark:text-white"
                                            placeholder="-3.7038"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <button 
                                            type="button"
                                            onClick={handleVerifyAddress}
                                            disabled={verifyingGeo}
                                            className={`w-full py-3 rounded-xl border-2 border-dashed transition-all font-bold text-sm flex items-center justify-center gap-2 ${
                                                geoResult 
                                                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/30 text-green-700 dark:text-green-400' 
                                                : 'bg-gray-50 dark:bg-slate-900/50 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-500/50 hover:text-primary-600 dark:hover:text-primary-400'
                                            }`}
                                        >
                                            {verifyingGeo ? (
                                                <>⌛ Verificando...</>
                                            ) : geoResult ? (
                                                <>📍 Ubicación confirmada (actualizar)</>
                                            ) : (
                                                <>🗺️ Obtener Coordenadas desde Dirección</>
                                            )}
                                        </button>
                                        {geoResult && (
                                            <p className="text-[10px] text-green-600 mt-1 font-bold text-center uppercase tracking-widest">
                                                Confirmado: {geoResult.displayName?.split(',').slice(0, 3).join(',')}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Customization Section - Services Only */}
                                <div className="border-t border-gray-100 dark:border-slate-700 pt-6">
                                    <h3 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-2">
                                        🎨 Personalización
                                    </h3>

                                     <div>
                                        <label className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3 block">Servicios</label>
                                        <div className="flex flex-wrap gap-2">
                                            {amenityList.map((a) => (
                                                <button
                                                    key={a.id}
                                                    type="button"
                                                    onClick={() => toggleAmenity(a.id)}
                                                    className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-bold border-2 transition-all ${formData.amenities.includes(a.id)
                                                        ? 'bg-primary-500 border-primary-500 text-white shadow-lg shadow-primary-500/30'
                                                        : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-primary-200 dark:hover:border-primary-500/50'
                                                        }`}
                                                >
                                                    {a.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 sm:pt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="py-3 sm:py-4 px-6 rounded-xl sm:rounded-2xl font-black uppercase tracking-widest text-sm text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all order-2 sm:order-1 sm:flex-1"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={mutation.isPending}
                                        className="py-3 sm:py-4 px-6 rounded-xl sm:rounded-2xl font-black uppercase tracking-widest text-sm bg-primary-600 text-white shadow-xl hover:bg-primary-700 transition-all transform hover:-translate-y-1 active:scale-95 disabled:opacity-50 order-1 sm:order-2 sm:flex-[2]"
                                    >
                                        {mutation.isPending ? 'Guardando...' : (editingClub ? 'Guardar' : 'Crear')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageClubs;
